import React, { HTMLAttributes, useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { toast } from 'react-toastify';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import {
  InsightViewCellColorCondition,
  InsightViewCellColorRuleItem,
} from 'types/api/SystemTenant/AROView/InsightViewDefinitionTypes';
import { INSIGHT_VIEW_CELL_COLOR_OPTIONS, MAP_COLOR_CODE_WITH_COLOR_HEXA } from 'constant/ViewConstant';

export const CELL_COLOR_CONDITION_OPTIONS: AutoCompleteItem[] = [
  { label: 'Greater Than', value: InsightViewCellColorCondition.GREATER_THAN },
  { label: 'Greater Than or Equal', value: InsightViewCellColorCondition.NUMERIC_GREATER_THAN_OR_EQUAL },
  { label: 'Less Than', value: InsightViewCellColorCondition.LESS_THAN },
  { label: 'Less Than or Equal', value: InsightViewCellColorCondition.NUMERIC_LESS_THAN_OR_EQUAL },
  { label: 'Equal', value: InsightViewCellColorCondition.EQUAL },
  { label: 'Between', value: InsightViewCellColorCondition.BETWEEN },
];

export const CONDITION_SYMBOL: Record<string, string> = {
  [InsightViewCellColorCondition.GREATER_THAN]: '>',
  [InsightViewCellColorCondition.NUMERIC_GREATER_THAN_OR_EQUAL]: '≥',
  [InsightViewCellColorCondition.LESS_THAN]: '<',
  [InsightViewCellColorCondition.NUMERIC_LESS_THAN_OR_EQUAL]: '≤',
  [InsightViewCellColorCondition.EQUAL]: '=',
  [InsightViewCellColorCondition.BETWEEN]: 'between',
};

export const isBetweenCondition = (c: AutoCompleteItem | null) =>
  c?.value === InsightViewCellColorCondition.BETWEEN;

const ModalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 520,
  bgcolor: 'background.paper',
  maxHeight: '90vh',
  overflow: 'auto',
  borderRadius: 1,
  py: 2,
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

const renderColorOption = (props: HTMLAttributes<HTMLElement>, option: AutoCompleteItem) => (
  <Box component='li' {...props}>
    <Stack direction='row' alignItems='center' gap={1}>
      <span
        style={{
          width: 18,
          height: 18,
          backgroundColor: MAP_COLOR_CODE_WITH_COLOR_HEXA.get(option.value.toString()),
          display: 'inline-block',
        }}
      />
      <span>{option.label}</span>
    </Stack>
  </Box>
);

export interface ModalCellColorRuleProps {
  visible?: boolean;
  onClose?: () => void;
  onSave?: (rule: InsightViewCellColorRuleItem) => void;
  initialData?: InsightViewCellColorRuleItem | null;
  attributeReferenceOptions: AutoCompleteItem[];
}

export function ModalCellColorRule(props: Readonly<ModalCellColorRuleProps>) {
  const { visible = false, onClose, onSave, initialData, attributeReferenceOptions } = props;

  const [condition, setCondition] = useState<AutoCompleteItem | null>(null);
  const [value, setValue] = useState<string>('');
  const [minValue, setMinValue] = useState<string>('');
  const [maxValue, setMaxValue] = useState<string>('');
  const [includeMin, setIncludeMin] = useState<boolean>(false);
  const [includeMax, setIncludeMax] = useState<boolean>(false);
  const [color, setColor] = useState<AutoCompleteItem | null>(null);
  const [attributeReference, setAttributeReference] = useState<AutoCompleteItem | null>(null);
  const [previewValue, setPreviewValue] = useState<string>('5');

  useEffect(() => {
    if (!visible) return;
    if (initialData) {
      setCondition(initialData.condition);
      setValue(initialData.value ?? '');
      setMinValue(initialData.minValue ?? '');
      setMaxValue(initialData.maxValue ?? '');
      setIncludeMin(!!initialData.includeMin);
      setIncludeMax(!!initialData.includeMax);
      setColor(initialData.color);
      setAttributeReference(initialData.attributeReference);
      setPreviewValue(initialData.value ?? initialData.minValue ?? '5');
    } else {
      setCondition(null);
      setValue('');
      setMinValue('');
      setMaxValue('');
      setIncludeMin(false);
      setIncludeMax(false);
      setColor(null);
      setAttributeReference(null);
      setPreviewValue('5');
    }
  }, [visible, initialData]);

  const between = isBetweenCondition(condition);

  const previewMatch: boolean | null = (() => {
    if (!condition) return null;
    const sample = parseFloat(previewValue);
    if (Number.isNaN(sample)) return null;
    if (between) {
      const min = parseFloat(minValue);
      const max = parseFloat(maxValue);
      if (Number.isNaN(min) || Number.isNaN(max)) return null;
      const minOk = includeMin ? sample >= min : sample > min;
      const maxOk = includeMax ? sample <= max : sample < max;
      return minOk && maxOk;
    }
    if (attributeReference) return null;
    if (!value) return null;
    const threshold = parseFloat(value);
    if (Number.isNaN(threshold)) return null;
    switch (condition.value) {
      case InsightViewCellColorCondition.EQUAL:
        return sample === threshold;
      case InsightViewCellColorCondition.GREATER_THAN:
        return sample > threshold;
      case InsightViewCellColorCondition.LESS_THAN:
        return sample < threshold;
      case InsightViewCellColorCondition.NUMERIC_GREATER_THAN_OR_EQUAL:
        return sample >= threshold;
      case InsightViewCellColorCondition.NUMERIC_LESS_THAN_OR_EQUAL:
        return sample <= threshold;
      default:
        return null;
    }
  })();

  const handleSave = () => {
    if (!condition) {
      toast.error('Please choose a condition');
      return;
    }
    if (!color) {
      toast.error('Please choose a color');
      return;
    }
    if (between) {
      if (!minValue) {
        toast.error('Please fill in Min');
        return;
      }
      if (!maxValue) {
        toast.error('Please fill in Max');
        return;
      }
      const min = parseFloat(minValue);
      const max = parseFloat(maxValue);
      if (!Number.isNaN(min) && !Number.isNaN(max) && min > max) {
        toast.error('Min must be less than or equal to Max');
        return;
      }
    } else if (!value) {
      toast.error('Please fill in Value');
      return;
    }

    const next: InsightViewCellColorRuleItem = {
      id: initialData?.id ?? null,
      condition,
      color,
      attributeReference,
      value: between ? null : value,
      minValue: between ? minValue : null,
      maxValue: between ? maxValue : null,
      includeMin: between ? includeMin : false,
      includeMax: between ? includeMax : false,
      priorityOrder: initialData?.priorityOrder ?? 0,
    };
    onSave?.(next);
  };

  return (
    <Modal open={visible} onClose={onClose}>
      <Stack direction='column' sx={ModalContentStyle} px={2}>
        <Stack justifyContent='space-between' direction='row' alignItems='center'>
          <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
            {initialData ? 'Edit Rule' : 'Add Rule'}
          </Typography>
          <IconButton onClick={onClose} component='label'>
            <HighlightOffRoundedIcon sx={closeIconStyle} />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          <Stack>
            <Typography variant='body2' fontWeight='bold' color='black' mb={0.5}>
              Condition
            </Typography>
            <ToggleButtonGroup
              value={condition?.value ?? null}
              exclusive
              onChange={(_, newValue) => {
                if (newValue == null) return;
                const opt = CELL_COLOR_CONDITION_OPTIONS.find((o) => o.value === newValue);
                if (opt) setCondition(opt);
              }}
              fullWidth
              size='small'
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  py: 1,
                  borderColor: '#D0D5DD',
                  color: '#475467',
                },
                '& .MuiToggleButton-root.Mui-selected': {
                  bgcolor: '#3B4797',
                  color: '#fff',
                  '&:hover': { bgcolor: '#2F3A7A' },
                },
              }}
            >
              {CELL_COLOR_CONDITION_OPTIONS.map((opt) => {
                const v = opt.value as string;
                const isWord = v === InsightViewCellColorCondition.BETWEEN;
                return (
                  <ToggleButton
                    key={v}
                    value={v}
                    title={opt.label}
                    aria-label={opt.label}
                  >
                    <span
                      style={{
                        fontSize: isWord ? 13 : 22,
                        fontWeight: 600,
                        lineHeight: 1,
                      }}
                    >
                      {CONDITION_SYMBOL[v]}
                    </span>
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Stack>

          {between ? (
            <Stack spacing={1.5}>
              <Stack direction='row' spacing={2}>
                <Stack flex={1}>
                  <Typography variant='body2' fontWeight='bold' color='black' mb={0.5}>
                    Min
                  </Typography>
                  <TextField
                    size='small'
                    value={minValue}
                    onChange={(e) => setMinValue(e.target.value)}
                    placeholder='0'
                  />
                </Stack>
                <Stack flex={1}>
                  <Typography variant='body2' fontWeight='bold' color='black' mb={0.5}>
                    Max
                  </Typography>
                  <TextField
                    size='small'
                    value={maxValue}
                    onChange={(e) => setMaxValue(e.target.value)}
                    placeholder='0'
                  />
                </Stack>
              </Stack>
              <Stack direction='row' spacing={4}>
                <Stack direction='row' alignItems='center'>
                  <Checkbox
                    checked={includeMin}
                    onChange={(e) => setIncludeMin(e.target.checked)}
                    sx={{ color: '#828C99', px: 0, paddingRight: 0.5 }}
                  />
                  <Typography variant='input-label-gray' fontWeight={600}>
                    Include Min
                  </Typography>
                </Stack>
                <Stack direction='row' alignItems='center'>
                  <Checkbox
                    checked={includeMax}
                    onChange={(e) => setIncludeMax(e.target.checked)}
                    sx={{ color: '#828C99', px: 0, paddingRight: 0.5 }}
                  />
                  <Typography variant='input-label-gray' fontWeight={600}>
                    Include Max
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          ) : (
            <Stack>
              <Typography variant='body2' fontWeight='bold' color='black' mb={0.5}>
                Value
              </Typography>
              <TextField
                size='small'
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder='0'
              />
            </Stack>
          )}

          <Stack>
            <Typography variant='body2' fontWeight='bold' color='black' mb={0.5}>
              Color
            </Typography>
            <Autocomplete
              size='small'
              value={color}
              onChange={(_, v) => setColor(v)}
              options={INSIGHT_VIEW_CELL_COLOR_OPTIONS}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderOption={renderColorOption}
              renderInput={(p) => <TextField {...p} placeholder='Select color' />}
            />
          </Stack>

          <Stack>
            <Typography variant='body2' fontWeight='bold' color='black' mb={0.5}>
              Attribute Reference (optional)
            </Typography>
            <Autocomplete
              size='small'
              value={attributeReference}
              onChange={(_, v) => setAttributeReference(v)}
              options={attributeReferenceOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(p) => <TextField {...p} placeholder='Select attribute reference' />}
            />
          </Stack>
        </Stack>

        <Box sx={{ p: 1.5, mt: 2, bgcolor: '#F5F6F8', borderRadius: 1 }}>
          <Typography variant='body2' fontWeight='bold' color='black' mb={0.75}>
            Preview
          </Typography>
          <Stack direction='row' alignItems='center' spacing={1.5} flexWrap='wrap'>
            <Typography variant='input-label-gray' fontWeight={600}>
              Try value:
            </Typography>
            <TextField
              size='small'
              value={previewValue}
              onChange={(e) => setPreviewValue(e.target.value)}
              sx={{ width: 90 }}
            />
            <Typography variant='body2' color='#828C99'>
              →
            </Typography>
            {attributeReference && !between ? (
              <Typography variant='input-label-gray' fontStyle='italic'>
                preview unavailable when comparing against another attribute
              </Typography>
            ) : previewMatch === null ? (
              <Typography variant='input-label-gray' fontStyle='italic'>
                fill in the rule to preview
              </Typography>
            ) : previewMatch ? (
              <Stack direction='row' alignItems='center' spacing={0.75}>
                <span
                  style={{
                    width: 22,
                    height: 22,
                    backgroundColor: color
                      ? MAP_COLOR_CODE_WITH_COLOR_HEXA.get(color.value.toString())
                      : '#E0E0E0',
                    display: 'inline-block',
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.12)',
                  }}
                />
                <Typography variant='body2' fontWeight={600} color='#1B7F45'>
                  Match{color ? ` → ${color.label}` : ''}
                </Typography>
              </Stack>
            ) : (
              <Typography variant='body2' fontWeight={600} color='#828C99'>
                No match
              </Typography>
            )}
          </Stack>
        </Box>

        <Stack direction='row' justifyContent='flex-end' spacing={2} sx={{ pt: 3 }}>
          <Button variant='main-table-panel-border' onClick={onClose}>
            Cancel
          </Button>
          <Button variant='main-table-panel' onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
